import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';

import { formatCPF } from '@wareclouds/frontend-common';
import { formatRut } from '@wareclouds/frontend-common/b2c';
import { DateCalendar, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import 'dayjs/locale/es';

import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import {
  Dialog,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
} from '@mui/material';

import { IS_BR } from '../../utils/registrationConfig';

const TODAY = dayjs();
const LOCALE = IS_BR ? 'pt-br' : 'es';

function BirthDateField({
  label,
  placeholder,
  value,
  onChange,
  error,
  helperText,
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TextField
        label={label}
        placeholder={placeholder}
        value={value ? dayjs(value).format('DD/MM/YYYY') : ''}
        variant="outlined"
        fullWidth
        error={error}
        helperText={helperText}
        onClick={() => setOpen(true)}
        slotProps={{
          input: {
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setOpen(true)} edge="end">
                  <CalendarTodayIcon sx={{ color: 'text.secondary' }} />
                </IconButton>
              </InputAdornment>
            ),
          },
          inputLabel: { shrink: true },
        }}
      />
      <Dialog open={open} onClose={() => setOpen(false)}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={LOCALE}>
          <DateCalendar
            value={value}
            maxDate={TODAY}
            views={['year', 'month', 'day']}
            openTo="year"
            onChange={(newValue, selectionState) => {
              onChange(newValue);
              if (selectionState === 'finish') {
                setOpen(false);
              }
            }}
          />
        </LocalizationProvider>
      </Dialog>
    </>
  );
}

export function PersonalInfoSection({
  control,
  errors,
  birthDateFieldRules,
  rutFieldRules,
  cpfFieldRules,
}) {
  const { t } = useTranslation(['onboarding']);
  const f = (key) => t(`registration.section.personal_info.fields.${key}`);

  return (
    <Stack spacing={2}>
      <Controller
        name="birth_date"
        control={control}
        rules={birthDateFieldRules}
        render={({ field }) => (
          <BirthDateField
            label={f('birth_date.label')}
            placeholder={f('birth_date.placeholder')}
            value={field.value}
            onChange={field.onChange}
            error={!!errors.birth_date}
            helperText={errors.birth_date?.message}
          />
        )}
      />

      {IS_BR ? (
        <>
          <Controller
            name="cpf"
            control={control}
            rules={cpfFieldRules}
            render={({ field }) => (
              <TextField
                {...field}
                label={f('cpf.label')}
                placeholder={f('cpf.placeholder')}
                variant="outlined"
                fullWidth
                error={!!errors.cpf}
                helperText={errors.cpf?.message}
                onChange={(e) => field.onChange(formatCPF(e.target.value))}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            )}
          />
        </>
      ) : (
        <Controller
          name="rut"
          control={control}
          rules={rutFieldRules}
          render={({ field }) => (
            <TextField
              {...field}
              label={f('rut.label')}
              placeholder={f('rut.placeholder')}
              variant="outlined"
              fullWidth
              error={!!errors.rut}
              helperText={errors.rut?.message}
              onChange={(e) => field.onChange(formatRut(e.target.value))}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          )}
        />
      )}

      {IS_BR && (
        <Controller
          name="neighborhood"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextField
              {...field}
              label={f('neighborhood.label')}
              placeholder={f('neighborhood.placeholder')}
              variant="outlined"
              fullWidth
              error={!!errors.neighborhood}
              helperText={errors.neighborhood?.message}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          )}
        />
      )}

      <Controller
        name="address"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <TextField
            {...field}
            label={f('address.label')}
            placeholder={f('address.placeholder')}
            variant="outlined"
            fullWidth
            error={!!errors.address}
            helperText={errors.address?.message}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        )}
      />
    </Stack>
  );
}
